import { useMemo } from 'react';
import { Url } from '@rossum/api-client';
import { EmailTemplate } from '@rossum/api-client/emailTemplates';
import { QueueRule } from '@rossum/api-client/schemas';
import { getReferencedEmailTemplateUrls } from '../utils/getReferencedEmailTemplateUrls';
import { useRuleEmailTemplatesByQueues } from './useRuleEmailTemplatesByQueues';

/**
 * Fetches email templates referenced by a rule's `send_email` actions across the rule's queues.
 *
 * Returns templates that exist in any of the rule's queues *and* are referenced by the rule.
 * Use {@link getUnavailableEmailTemplates} to filter for a specific target queue.
 */
export const useReferencedEmailTemplates = (rule: QueueRule | null) => {
  const referencedTemplateUrls = useMemo(
    () => (rule ? getReferencedEmailTemplateUrls(rule) : []),
    [rule]
  );

  const { data: ruleEmailTemplates } = useRuleEmailTemplatesByQueues(
    referencedTemplateUrls.length > 0 && rule ? rule.queues : []
  );

  return useMemo<EmailTemplate[]>(() => {
    if (!ruleEmailTemplates) return [];
    const referenced = new Set(referencedTemplateUrls);
    return ruleEmailTemplates.filter(t => referenced.has(t.url));
  }, [ruleEmailTemplates, referencedTemplateUrls]);
};

/**
 * Returns referenced templates that are *not* available in the given target queue
 * (i.e. their `send_email` action would not run there).
 */
export const getUnavailableEmailTemplates = (
  referencedTemplates: EmailTemplate[],
  targetQueueUrl: Url
): EmailTemplate[] =>
  referencedTemplates.filter(t => t.queue !== targetQueueUrl);
